import { API_BASE_URL, api } from './client'

/**
 * Cấu hình nhận diện khuôn mặt có HAI surface backend song song, cùng một
 * nghiệp vụ:
 *
 *   - 'staff' → /growers/:id/faces…                 (quyền growers.*)
 *   - 'my'    → /care-logs/my-growers/:id/faces…    (quyền care_logs.read)
 *
 * Tách ra vì role `grower` cố ý không có growers.read: quyền đó không giới hạn
 * phạm vi và sẽ lộ PII của mọi hộ. Trên giao diện vẫn là một màn; chỉ base path
 * đổi theo vai trò người đang đăng nhập.
 */
export type FaceScope = 'staff' | 'my'

export function faceBasePath(growerId: string, scope: FaceScope): string {
    return scope === 'my'
        ? `/care-logs/my-growers/${growerId}/faces`
        : `/growers/${growerId}/faces`
}

/** Một ảnh đã đăng ký. Vector không bao giờ ra tới frontend. */
export type FacePhoto = {
    id: string
    file_id: string
    created_at: string
}

/** Một NGƯỜI trong hộ. Một hộ có nhiều bản ghi này. */
export type GrowerFace = {
    id: string
    grower_id: string
    full_name: string
    consent_at: string
    photo_count: number
    /** Từ 3 ảnh trở lên. Dưới ngưỡng đó thì chưa nhận diện được. */
    is_usable: boolean
    photos: FacePhoto[]
}

/**
 * Kết quả xem trước một khung hình lúc đang căn khuôn mặt.
 *
 * `status` dùng chung bộ chuỗi với lý do từ chối lúc chụp thật, nên giao diện
 * nói cùng một ngôn ngữ ở cả hai chỗ.
 */
export type FaceDetectStatus =
    | 'ok'
    | 'no_face'
    | 'multiple_faces'
    | 'face_too_small'
    | 'too_blurry'

export type FaceDetectResult = {
    status: FaceDetectStatus
    face_width_ratio: number
    /**
     * Độ quay đầu sang ngang, khoảng [-1, 1]. Quanh 0 là nhìn thẳng, dương là
     * mặt quay về phía cạnh PHẢI của ảnh, âm là về cạnh TRÁI.
     *
     * Cố ý không phải đơn vị độ — xem chú thích ở service Python.
     */
    yaw_ratio: number
}

export type FaceMatch = {
    face_id: string
    full_name: string
    score_percent: number
}

export type FaceTestMatch = {
    threshold_percent: number
    /** Kết luận tính ở backend, frontend không tự so ngưỡng. */
    matched: boolean
    best?: FaceMatch
    results: FaceMatch[]
}

export type CreateFaceBody = {
    full_name: string
    /** Bắt buộc true; backend tự đóng dấu thời điểm đồng ý. */
    consent: boolean
}

export type UpdateFaceBody = {
    full_name: string
}

export const growerFacesApi = {
    list: (growerId: string, scope: FaceScope) =>
        api.get<GrowerFace[]>(faceBasePath(growerId, scope)),

    create: (growerId: string, scope: FaceScope, body: CreateFaceBody) =>
        api.post<GrowerFace>(faceBasePath(growerId, scope), body),

    update: (
        growerId: string,
        scope: FaceScope,
        faceId: string,
        body: UpdateFaceBody,
    ) =>
        api.patch<GrowerFace>(
            `${faceBasePath(growerId, scope)}/${faceId}`,
            body,
        ),

    remove: (growerId: string, scope: FaceScope, faceId: string) =>
        api.delete<void>(`${faceBasePath(growerId, scope)}/${faceId}`),

    addPhoto: (
        growerId: string,
        scope: FaceScope,
        faceId: string,
        blob: Blob,
    ) => {
        const form = new FormData()
        // Tên tệp chỉ để log; nội dung mới là thứ backend đọc.
        form.append('image', blob, 'face.jpg')
        return api.post<FacePhoto>(
            `${faceBasePath(growerId, scope)}/${faceId}/photos`,
            form,
        )
    },

    removePhoto: (
        growerId: string,
        scope: FaceScope,
        faceId: string,
        photoId: string,
    ) =>
        api.delete<void>(
            `${faceBasePath(growerId, scope)}/${faceId}/photos/${photoId}`,
        ),

    /**
     * Thay TOÀN BỘ bộ ảnh của một người bằng bộ vừa chụp.
     *
     * Đăng ký lại là chụp lại cả bộ chứ không cộng dồn. THỨ TỰ CÓ NGHĨA: tấm 1
     * chính diện, tấm 2 nghiêng trái, tấm 3 nghiêng phải.
     */
    replacePhotos: (
        growerId: string,
        scope: FaceScope,
        faceId: string,
        blobs: Blob[],
    ) => {
        const form = new FormData()
        blobs.forEach((blob, i) =>
            form.append('image', blob, `face-${i + 1}.jpg`),
        )
        return api.put<FacePhoto[]>(
            `${faceBasePath(growerId, scope)}/${faceId}/photos`,
            form,
        )
    },

    /**
     * Hỏi server khung hình đang căn có chụp được không.
     *
     * KHÔNG theo scope: endpoint không đụng dữ liệu hộ nào nên backend chỉ có
     * một đường duy nhất cho cả nhân viên lẫn tài khoản hộ.
     */
    detectFrame: (blob: Blob, signal?: AbortSignal) => {
        const form = new FormData()
        form.append('image', blob, 'frame.jpg')
        return api.post<FaceDetectResult>('/faces/detect', form, { signal })
    },

    testMatch: (growerId: string, scope: FaceScope, blob: Blob) => {
        const form = new FormData()
        form.append('image', blob, 'probe.jpg')
        return api.post<FaceTestMatch>(
            `${faceBasePath(growerId, scope)}/test-match`,
            form,
        )
    },
}

/**
 * URL xem một ảnh khuôn mặt.
 *
 * KHÔNG dùng /files/:id/download: route đó gác bằng quyền `files.read`, mà role
 * `grower` cố ý không có (quyền đó không giới hạn phạm vi). Route dưới đây nằm
 * trong hộ và tự kiểm ảnh thuộc đúng khuôn mặt.
 */
export function facePhotoUrl(
    growerId: string,
    scope: FaceScope,
    faceId: string,
    photoId: string,
): string {
    return `${API_BASE_URL}${faceBasePath(growerId, scope)}/${faceId}/photos/${photoId}/download`
}

export type FaceRecognitionSettings = {
    match_threshold: number
    match_threshold_percent: number
}

export const faceSettingsApi = {
    get: () =>
        api.get<FaceRecognitionSettings>('/admin/face-recognition/settings'),
    update: (matchThreshold: number) =>
        api.put<FaceRecognitionSettings>('/admin/face-recognition/settings', {
            match_threshold: matchThreshold,
        }),
}
